import { Selector } from '@ngxs/store';

import { Account, ALL } from '@efficiently/models';

import { OrganizationFilter } from '@efficiently/client-api-projects';
import { OrganizationSettingsSelectors } from '@efficiently/client-domain-application-settings';
import { OrgAdminSelectors } from '@efficiently/client-domain-org-admin';
import type { ProjectPaginationInput } from '@efficiently/client-domain-projects';
import { AutocompleteSearchOption } from '@efficiently/client-ui-common';

import { ProjectListState } from './project-list-state';
import { ProjectFilters, ProjectListStateModel, ProjectOwner, ProjectUi } from './project-list-state.model';

export class ProjectListSelectors {
	@Selector([ProjectListState])
	public static pending(state: ProjectListStateModel): boolean {
		return state.pending;
	}

	@Selector([ProjectListState])
	public static projects(state: ProjectListStateModel): ProjectUi[] {
		return state.projects;
	}

	@Selector([ProjectListState])
	public static clonedProjectId(state: ProjectListStateModel): string | null {
		return state.clonedProjectId;
	}

	@Selector([ProjectListState])
	public static projectOwners(state: ProjectListStateModel): ProjectOwner[] {
		return state.projectOwners;
	}

	@Selector([ProjectListState])
	public static organizations(state: ProjectListStateModel): OrganizationFilter[] {
		return state.organizations;
	}

	@Selector([ProjectListState])
	public static autocompleteSearchOptions(state: ProjectListStateModel): AutocompleteSearchOption[] {
		return state.autocompleteSearchOptions;
	}

	@Selector([ProjectListState])
	public static filters(state: ProjectListStateModel): ProjectFilters {
		return state.filters;
	}

	@Selector([ProjectListState])
	public static pagination(state: ProjectListStateModel): ProjectPaginationInput {
		return state.pagination;
	}

	@Selector([ProjectListState])
	public static statusFilter(state: ProjectListStateModel): string {
		return state.filters?.status;
	}

	@Selector([ProjectListState])
	public static searchFilter(state: ProjectListStateModel): string {
		return state.filters?.searchQuery;
	}

	@Selector([ProjectListState])
	public static ownerFilter(state: ProjectListStateModel): string {
		return state.filters?.owner;
	}

	@Selector([ProjectListState])
	public static isFetchingAutocomplete(state: ProjectListStateModel): boolean {
		return state.isFetchingAutocomplete;
	}

	@Selector([
		ProjectListSelectors.organizations,
		OrganizationSettingsSelectors.defaultOrganizationId,
		OrgAdminSelectors.account,
	])
	public static organizationsList(
		organizations: OrganizationFilter[],
		defaultOrganizationId: string,
		account: Account,
	): OrganizationFilter[] {
		const defaultOrganizationList: OrganizationFilter[] = [
			{
				id: ALL,
				name: ALL,
				isDefault: ALL === defaultOrganizationId,
			},
			{
				id: account.orgId,
				name: account.name,
				isDefault: account.orgId === defaultOrganizationId,
			},
		];
		return [...defaultOrganizationList, ...organizations].map(org => ({
			...org,
			isDefault: org.id === defaultOrganizationId,
		}));
	}
}
